/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, type JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type Client } from '../../lib/api/resources.js';
import { useAssignTherapist, useTherapists } from '../../lib/queries/clinical.js';

const ASSIGN_SELECT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/** Assign / re-assign the patient's therapist (no session booked — a plain link). */
export function AssignTherapistControl({ client }: { readonly client: Client }): JSX.Element {
  const { t } = useTranslation();
  const therapists = useTherapists();
  const assign = useAssignTherapist();
  const [open, setOpen] = useState(false);
  const [therapistId, setTherapistId] = useState('');

  const openModal = useCallback(() => {
    setTherapistId('');
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onSelect = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTherapistId(e.target.value);
  }, []);
  const onAssign = useCallback(() => {
    if (therapistId === '') {
      return;
    }
    assign.mutate({ id: client.id, therapistId }, { onSuccess: closeModal });
  }, [assign, client.id, therapistId, closeModal]);

  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">
        {t('admin.clientDetail.therapist')}
      </p>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {client.therapistName ?? '—'}
        </p>
        <button
          type="button"
          onClick={openModal}
          className="focus-ring rounded text-xs font-medium text-[var(--accent)] hover:underline"
        >
          {client.therapistName !== null
            ? t('admin.clientDetail.changeTherapist')
            : t('admin.clientDetail.assignTherapist')}
        </button>
      </div>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('admin.clientDetail.assignTitle')}
        description={t('admin.clientDetail.assignSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <div className="space-y-4">
          <select
            value={therapistId}
            onChange={onSelect}
            aria-label={t('admin.clientDetail.assignTitle')}
            className={ASSIGN_SELECT}
          >
            <option value="" disabled>
              {t('intake.schedule.selectTherapist')}
            </option>
            {(therapists.data ?? []).map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
          {assign.isError && (
            <p role="alert" className="text-sm text-red-600">
              {t('common.saveError')}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={onAssign}
              disabled={therapistId === '' || assign.isPending}
            >
              {t('admin.clientDetail.assignAction')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
